import {
  HMAnnotation,
  HMBlock,
  HMBlockImage,
  HMBlockNode,
  HMBlockQuery,
  HMContactRecord,
  HMDocument,
  HMDocumentInfo,
} from '@seed-hypermedia/client/hm-types'
import {entityQueryPathToHmIdPath, normalizeDate, unpackHmId} from './utils'

// Check if a block has meaningful content
export function hasBlockContent(block: HMBlockNode): boolean {
  const blockData = block.block

  // Check for children first
  if (block.children && block.children.length > 0) {
    return true
  }

  // Check based on block type
  switch (blockData.type) {
    case 'Paragraph':
    case 'Heading':
    case 'Code':
    case 'Math':
      // Text-based blocks: check if text is not empty
      return !!blockData.text && blockData.text.trim().length > 0

    case 'Image':
    case 'Video':
    case 'File':
    case 'Button':
    case 'Embed':
    case 'WebEmbed':
    case 'Nostr':
      // Link-based blocks: these always have content if they have a link
      return !!blockData.link

    case 'Query':
      // Query blocks always have content
      return true

    case 'Group':
    case 'Link':
      // Group and Link blocks are structural, check text if available
      return !!('text' in blockData && blockData.text && blockData.text.trim().length > 0)

    case 'Slot':
      // Slot is an invisible grouping container with no content of its own.
      return false

    default:
      return false
  }
}

// HMBlockNodes are recursive values. we want the output to have the same shape, but limit the total number of blocks
// the first blocks will be included up until the totalBlock value is reached
export function clipContentBlocks(content: HMBlockNode[] | undefined, totalBlocks: number): HMBlockNode[] | null {
  if (!content) return null
  const output: HMBlockNode[] = []
  let blocksRemaining: number = totalBlocks
  function walk(currentNode: HMBlockNode, outputNode: HMBlockNode[]): void {
    if (blocksRemaining <= 0) {
      return
    }
    let newNode: HMBlockNode = {
      block: currentNode.block,
      children: currentNode.children ? [] : undefined,
    }
    outputNode.push(newNode)
    blocksRemaining--
    if (currentNode.children && newNode.children) {
      for (let child of currentNode.children) {
        walk(child, newNode.children)
      }
    }
  }
  for (let root of content) {
    walk(root, output)
  }
  return output
}

export function getDocumentTitle(document?: Pick<HMDocument, 'metadata' | 'path'> | null) {
  if (!document) {
    return 'Error: document not found'
  }

  return document.metadata?.name || entityQueryPathToHmIdPath(document.path)?.at(-1) || ''
}

export function getContactMetadata(
  accountUid: string,
  metadata: HMDocument['metadata'] | null | undefined,
  contacts?: HMContactRecord[] | null,
) {
  const contact = contacts?.find((c) => c.subject === accountUid)
  if (contact) {
    // Only use contact.name if it's not empty, otherwise fall back to metadata name
    return {...(metadata || {}), name: contact.name || metadata?.name || 'Untitled Contact'}
  }
  return {...(metadata || {}), name: metadata?.name || 'Untitled Contact'}
}

export function getMetadataName(metadata?: HMDocument['metadata'] | null) {
  return metadata?.name || 'Untitled Document'
}

export function getAccountName(document: HMDocument | null | undefined) {
  if (!document) return ''
  if (document.metadata.name) return document.metadata.name
  if (document.account) return `${document.account.slice(0, -6)}`
  return '?'
}

function lastUpdateOfEntry(entry: HMDocumentInfo) {
  return normalizeDate(entry.updateTime)?.getTime() || 0
}

function createTimeOfEntry(entry: HMDocumentInfo) {
  return normalizeDate(entry.createTime)?.getTime() || 0
}

function displayPublishTimeOfEntry(entry: HMDocumentInfo): number {
  const dateStr = entry.metadata.displayPublishTime
  if (dateStr) {
    const timestamp = Date.parse(dateStr)
    if (!isNaN(timestamp)) return timestamp
  }
  return normalizeDate(entry.updateTime)?.getTime() || 0
}

function activityTimeOfEntry(entry: HMDocumentInfo): number {
  const activity = entry.activitySummary
  const changeTime = normalizeDate(activity?.latestChangeTime)?.getTime() || 0
  const commentTime = normalizeDate(activity?.latestCommentTime)?.getTime() || 0
  return Math.max(changeTime, commentTime) || normalizeDate(entry.updateTime)?.getTime() || 0
}

function titleOfEntry(entry: HMDocumentInfo) {
  return entry.metadata.name
}

export function queryBlockSortedItems({
  entries,
  sort,
}: {
  entries: Array<HMDocumentInfo>
  sort: NonNullable<HMBlockQuery['attributes']['query']['sort']>
}) {
  if (!entries?.length) return entries ?? []
  if (sort.length !== 1) return entries
  const rule = sort[0]
  if (!rule) return entries
  const {term, reverse} = rule

  const valueOf = (entry: HMDocumentInfo): string | number => {
    switch (term) {
      case 'title':
        return titleOfEntry(entry) || ''
      case 'path':
        return entry.path?.join('/') || ''
      case 'created':
        return createTimeOfEntry(entry)
      case 'updated':
        return lastUpdateOfEntry(entry)
      case 'displayTime':
        return displayPublishTimeOfEntry(entry)
      case 'activity':
        return activityTimeOfEntry(entry)
      default:
        return ''
    }
  }

  const res = [...entries].sort((a, b) => {
    const aValue = valueOf(a)
    const bValue = valueOf(b)
    if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue
    return String(aValue).localeCompare(String(bValue), undefined, {sensitivity: 'base', numeric: true})
  })

  return reverse ? res.reverse() : res
}

/** A content reference, retaining explicit inline mention identity when present. */
export type RefDefinition = {
  mentionKind?: 'account' | 'document'
  blockId: string
  link: string
  refId: any
}

export function extractRefs(children: HMBlockNode[], skipCards?: boolean): RefDefinition[] {
  let refs: RefDefinition[] = []
  function extractRefsFromBlock(block: HMBlockNode) {
    if (block.block?.type === 'Embed' && block.block.link) {
      if (block.block.attributes?.view === 'Card' && skipCards) return
      const refId = unpackHmId(block.block.link)
      if (refId)
        refs.push({
          blockId: block.block.id,
          link: block.block.link,
          refId,
        })
    }
    // @ts-expect-error
    ;(block.block as any).annotations?.forEach((annotation) => {
      if (annotation.type === 'Embed') {
        refs.push({
          blockId: block.block.id,
          link: annotation.link,
          refId: unpackHmId(annotation.link),
          ...(annotation.attributes?.mentionKind ? {mentionKind: annotation.attributes.mentionKind} : {}),
        })
      }
    })
    if (block.children) {
      block.children.forEach(extractRefsFromBlock)
    }
  }
  children.forEach(extractRefsFromBlock)
  return refs
}

/** Extracts direct document references from block links and inline link/embed annotations. */
export function extractAllContentRefs(children: HMBlockNode[]): RefDefinition[] {
  let refs: RefDefinition[] = []
  function walk(block: HMBlockNode) {
    if (block.block && 'link' in block.block && block.block.link) {
      const refId = unpackHmId(block.block.link)
      if (refId)
        refs.push({
          blockId: block.block.id,
          link: block.block.link,
          refId,
        })
    }
    // @ts-expect-error
    ;(block.block as any).annotations?.forEach((annotation) => {
      if (annotation.type === 'Embed' && annotation.link) {
        refs.push({
          blockId: block.block.id,
          link: annotation.link,
          refId: unpackHmId(annotation.link),
          ...(annotation.attributes?.mentionKind ? {mentionKind: annotation.attributes.mentionKind} : {}),
        })
      }
      if (annotation.type === 'Link' && annotation.link) {
        const refId = unpackHmId(annotation.link)
        if (refId) {
          refs.push({
            blockId: block.block.id,
            link: annotation.link,
            refId,
          })
        }
      }
    })
    if (block.children) {
      block.children.forEach(walk)
    }
  }
  children.forEach(walk)
  return refs
}

function isSelfQueryInclude(
  include: HMBlockQuery['attributes']['query']['includes'][number] | undefined,
  parentUid: string,
  parentPath: string[] | null,
): boolean {
  if (!include) return false
  if (include.space !== parentUid) return false
  const includePath = entityQueryPathToHmIdPath(include.path)
  const parentPathStr = (parentPath || []).join('/')
  const includePathStr = (includePath || []).join('/')
  return includePathStr === parentPathStr
}

export function hasQueryBlockTargetingSelf(
  children: HMBlockNode[],
  parentUid: string,
  parentPath: string[] | null,
): boolean {
  const queryBlocks = extractQueryBlocks(children)
  return queryBlocks.some((qb) =>
    qb.attributes.query.includes.some((include) => isSelfQueryInclude(include, parentUid, parentPath)),
  )
}

export function findSelfQueryBlock(
  children: HMBlockNode[],
  parentUid: string,
  parentPath: string[] | null,
): HMBlockQuery | null {
  const queryBlocks = extractQueryBlocks(children)
  for (const qb of queryBlocks) {
    if (qb.attributes.query.includes.some((include) => isSelfQueryInclude(include, parentUid, parentPath))) {
      return qb
    }
  }
  return null
}

const DEFAULT_QUERY_INCLUDES = '[{"space":"","path":"","mode":"Children"}]'

type EditorBlockLike = {
  type?: string
  props?: Record<string, any>
  children?: any[]
  [key: string]: any
}

export function hasSelfQueryBlockInEditorContent(
  blocks: EditorBlockLike[] | undefined | null,
  parentUid: string,
  parentPath: string[] | null,
): boolean {
  if (!blocks?.length) return false
  for (const block of blocks) {
    if (block?.type === 'query') {
      const raw = block.props?.queryIncludes ?? DEFAULT_QUERY_INCLUDES
      let includes: Array<{space?: string; path?: string; mode?: string}> = []
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) includes = parsed
      } catch {
        includes = []
      }
      if (includes.some((include) => isSelfQueryInclude(include as any, parentUid, parentPath))) {
        return true
      }
    }
    if (block?.children?.length && hasSelfQueryBlockInEditorContent(block.children, parentUid, parentPath)) {
      return true
    }
  }
  return false
}

export function extractQueryBlocks(children: HMBlockNode[]): HMBlockQuery[] {
  let queries: HMBlockQuery[] = []
  function extractQueriesFromBlock(block: HMBlockNode) {
    if (block.block?.type === 'Query') {
      queries.push(block.block)
    }
    if (block.children) {
      block.children.forEach(extractQueriesFromBlock)
    }
  }
  children.forEach(extractQueriesFromBlock)
  return queries
}

export function plainTextOfContent(content?: HMBlockNode[]): string {
  let textContent = ''
  content?.forEach((bn) => {
    if ((bn.block as any)?.text) {
      textContent += (bn.block as any)?.text + ' '
    }
  })
  return textContent
}

export function getDocumentImage(document: HMDocument): string | null {
  const coverImage = document.metadata.cover
  if (coverImage) return coverImage
  const firstImageBlock = findFirstBlock<HMBlockImage>(
    document.content,
    (block): block is HMBlockImage => block.type === 'Image' && !!(block as any).link,
  )
  if (firstImageBlock) return firstImageBlock.link || null
  return null
}

export function findFirstBlock<ResultBlockType extends HMBlock>(
  content: HMBlockNode[],
  test: (block: HMBlock) => block is ResultBlockType,
): ResultBlockType | null {
  let found: ResultBlockType | null = null
  let index = 0
  while (!found && index < content.length) {
    const blockNode = content[index]
    // @ts-ignore
    if (test(blockNode.block)) {
      // @ts-ignore
      found = blockNode.block
      break
    }
    const foundChild =
      // @ts-ignore
      blockNode.children && findFirstBlock(blockNode.children, test)
    if (foundChild) {
      found = foundChild
      break
    }
    index++
  }
  return found
}

export function getAnnotations(block: HMBlock): HMAnnotation[] | undefined {
  if (block.type === 'Paragraph') return block.annotations || []
  if (block.type === 'Heading') return block.annotations || []
  if (block.type === 'Embed') return block.annotations || []
  if (block.type === 'Video') return block.annotations || []
  if (block.type === 'File') return block.annotations || []
  if (block.type === 'Image') return block.annotations || []
  if (block.type === 'Query') return block.annotations || []
  if (block.type === 'Math') return block.annotations || []
  if (block.type === 'Code') return block.annotations || []
  if (block.type === 'Button') return block.annotations || []
  return undefined
}

export function getBlockText(block: HMBlock): string {
  if (block.type === 'Paragraph') return block.text || ''
  if (block.type === 'Heading') return block.text || ''
  if (block.type === 'Embed') return block.text || ''
  if (block.type === 'Video') return block.text || ''
  if (block.type === 'File') return block.text || ''
  if (block.type === 'Image') return block.text || ''
  if (block.type === 'Query') return block.text || ''
  if (block.type === 'Math') return block.text || ''
  if (block.type === 'Code') return block.text || ''
  if (block.type === 'Button') return block.text || ''
  return ''
}
